/**
 * What OS sets about money during setup (L42 and L43 of
 * docs/plans/ledger.md), worked out the same way by the setup tool that tells
 * OS what to say and by Build, which writes it to `app.tenant`.
 *
 * `app.setup_session.money` holds what the person said (`SetupMoneyNote`):
 * the country they named, when their financial year starts, and whether they
 * are registered for the country's tax. Everything else is read: a GSTIN or a
 * TRN kept in business details, the device's country, the currency, and file
 * names such as "Sales 2025-26.xlsx", and the `tax_regime` business detail.
 */
import {
  chooseFiscalYear,
  fiscalYearEvidenceInName,
  fiscalYearStartText,
  parseFiscalYearStart,
  type FiscalYearChoice,
  type FiscalYearEvidence,
} from './fiscal-year';
import { countryOfBusiness, isTaxRegime, taxCountryOf, type FiscalYearStart, type TaxName, type TaxRegime } from './tax-countries';

/** What the person said about money in setup; any part may be unsaid. */
export type SetupMoneyNote = {
  /** ISO 3166-1 alpha-2: the country they named, or their device's. */
  readonly country: string | null;
  /** When their financial year starts, as they said it, `MM-DD`. */
  readonly fiscalYearStart: string | null;
  /** Their answer to "Are you registered for GST?" (or VAT). */
  readonly registered: boolean | null;
};

export const EMPTY_SETUP_MONEY: SetupMoneyNote = { country: null, fiscalYearStart: null, registered: null };

/** A stored note, read without trusting it: a part that does not read is unsaid. */
export const readSetupMoney = (raw: unknown): SetupMoneyNote => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return EMPTY_SETUP_MONEY;
  const held = raw as Record<string, unknown>;
  const country = typeof held.country === 'string' && /^[A-Z]{2}$/.test(held.country) ? held.country : null;
  const start = typeof held.fiscalYearStart === 'string' ? parseFiscalYearStart(held.fiscalYearStart) : null;
  return {
    country,
    fiscalYearStart: start === null ? null : fiscalYearStartText(start),
    registered: typeof held.registered === 'boolean' ? held.registered : null,
  };
};

/** A note with the newer parts laid over: a part left null keeps what was said. */
export const mergeSetupMoney = (held: SetupMoneyNote, next: Partial<SetupMoneyNote>): SetupMoneyNote => ({
  country: next.country ?? held.country,
  fiscalYearStart: next.fiscalYearStart ?? held.fiscalYearStart,
  registered: next.registered ?? held.registered,
});

export type SetupMoneyInput = {
  readonly note: SetupMoneyNote;
  /** The device's country, a hint (`AgentChatContext.region`). */
  readonly region?: string | null;
  readonly currency?: string | null;
  /** A GSTIN and a TRN noted or kept in business details. */
  readonly gstin?: string | null;
  readonly trn?: string | null;
  /** The names of the person's own files: a name like "2025-26" is evidence of the year. */
  readonly fileNames?: readonly string[];
  /** A document series' start month already on file, when there is one. */
  readonly seriesStartMonth?: number | null;
  /** The `tax_regime` business detail, when one is on file: gst_regular, gst_composition, vat or none. */
  readonly detailRegime?: string | null;
};

/**
 * What OS does about tax (L43): set the regime, or ask the one question
 * ("Are you registered for GST?", or for VAT) by the country's tax name.
 */
export type TaxRegimeChoice =
  | { readonly kind: 'set'; readonly regime: TaxRegime; readonly source: 'gstin' | 'trn' | 'detail' | 'answer' | 'country' }
  | { readonly kind: 'ask'; readonly taxName: TaxName };

export type SetupMoneyDecision = {
  readonly country: string | null;
  readonly year: FiscalYearChoice;
  readonly tax: TaxRegimeChoice;
};

/** The `tax_regime` business detail's choices, as the workspace's regime. The composition scheme is `none` for now (L43). */
const DETAIL_REGIME: Readonly<Record<string, TaxRegime>> = { gst_regular: 'in_gst', gst_composition: 'none', vat: 'single', none: 'none' };

/**
 * The rule of L43: a GSTIN means in_gst and a TRN means single, with no
 * question; then the regime on file in business details; then the person's
 * answer, by the country's tax; a country with no such tax is none. Unknown
 * otherwise: OS asks, by the country's tax name (GST where nothing says).
 */
export const chooseTaxRegime = (input: {
  readonly country: string | null;
  readonly registered: boolean | null;
  readonly gstin?: string | null;
  readonly trn?: string | null;
  readonly detailRegime?: string | null;
}): TaxRegimeChoice => {
  if (typeof input.gstin === 'string' && input.gstin.trim() !== '') return { kind: 'set', regime: 'in_gst', source: 'gstin' };
  if (typeof input.trn === 'string' && input.trn.trim() !== '') return { kind: 'set', regime: 'single', source: 'trn' };
  const fromDetail = typeof input.detailRegime === 'string' ? DETAIL_REGIME[input.detailRegime] : undefined;
  if (fromDetail !== undefined) return { kind: 'set', regime: fromDetail, source: 'detail' };
  const row = taxCountryOf(input.country);
  if (row !== null && row.regime === 'none') return { kind: 'set', regime: 'none', source: 'country' };
  if (input.registered === false) return { kind: 'set', regime: 'none', source: 'answer' };
  if (input.registered === true && row !== null) return { kind: 'set', regime: row.regime, source: 'answer' };
  return { kind: 'ask', taxName: row?.taxName ?? 'gst' };
};

/** The year's evidence from what was said and read, the person's own words first. */
const yearEvidence = (input: SetupMoneyInput): FiscalYearEvidence[] => {
  const said = parseFiscalYearStart(input.note.fiscalYearStart);
  const series = input.seriesStartMonth;
  return [
    ...(said === null ? [] : [{ kind: 'start' as const, start: said, from: 'said' as const }]),
    ...(typeof series === 'number' && series >= 1 && series <= 12 ? [{ kind: 'start' as const, start: { month: series, day: 1 }, from: 'series' as const }] : []),
    ...(input.fileNames ?? []).flatMap((name) => fiscalYearEvidenceInName(name) ?? []),
  ];
};

/** The country, then the year by the rule of L42. */
export const decideSetupMoney = (input: SetupMoneyInput): SetupMoneyDecision => {
  const country = countryOfBusiness({
    gstin: input.gstin ?? null,
    trn: input.trn ?? null,
    region: input.note.country ?? input.region ?? null,
    currency: input.currency ?? null,
  });
  return {
    country,
    year: chooseFiscalYear({ country, evidence: yearEvidence(input) }),
    tax: chooseTaxRegime({ country, registered: input.note.registered, gstin: input.gstin ?? null, trn: input.trn ?? null, detailRegime: input.detailRegime ?? null }),
  };
};

/**
 * The year Build writes: the one OS set, else the suggestion of the question
 * nobody answered, so every workspace counts its year from somewhere; the
 * owner may change it freely until the first close (L42).
 */
export const buildFiscalYearStart = (decision: SetupMoneyDecision): FiscalYearStart =>
  decision.year.kind === 'set' ? decision.year.start : decision.year.suggested;

/**
 * The regime Build writes: the one OS set, else `none` for a question nobody
 * answered, so no tax is added to a bill until the owner says they are
 * registered, in the Money section or to OS.
 */
export const buildTaxRegime = (decision: SetupMoneyDecision): TaxRegime =>
  decision.tax.kind === 'set' && isTaxRegime(decision.tax.regime) ? decision.tax.regime : 'none';
